import { ReactNode, FC, useMemo } from "react"
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
  QueryCache,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import toast from "react-hot-toast"
import { isValidationError, ValidationResponse } from "@/api/validation"
import { useGlobalValidation } from "@/providers/validation"
import { router } from "@/router"

// Create query client factory that takes validation functions
const createQueryClient = (
  addValidationErrors: (response: ValidationResponse) => void,
) =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const status = (error as any).status

        // Handle 403 Forbidden by redirecting to unauthorized page
        if (status === 403) {
          window.location.href = "/unauthorized"
          return
        }

        // Handle 404 Not Found on /me endpoint - user cannot be found
        // This could be database down, user doesn't exist, etc.
        if (status === 404 && query.queryKey[0] === "me") {
          // Save the current page so user can retry from where they were
          sessionStorage.setItem("unavailable_from", window.location.pathname)
          router.navigate({ to: "/unavailable" })
          return
        }

        // Handle other 404s
        if (status === 404) {
          toast.error("Not found")
          return
        }

        // Handle 503 Service Unavailable
        if (status === 503) {
          toast.error("Service temporarily unavailable")
          return
        }

        toast.error(error.message)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error: any, _variables, _context, mutation) => {
        console.log(error)
        const status = (error as any).status

        // Check if the mutation has suppressErrorToast in meta
        const suppressErrorToast = mutation.options.meta?.suppressErrorToast

        // Handle 403 Forbidden by redirecting to unauthorized page
        if (status === 403) {
          window.location.href = "/unauthorized"
          return
        }
        // Handle 503 Service Unavailable with a user-friendly message
        if (status === 503) {
          if (!suppressErrorToast) {
            toast.error("Service temporarily unavailable")
          }
          return
        }
        if (status === 409) {
          if (!suppressErrorToast) {
            toast.error("Concurrent modification - refresh the data")
          }
          return
        }

        // Handle validation errors consistently
        if (isValidationError(error)) {
          // Process field validation errors globally
          addValidationErrors(error.response as ValidationResponse)
          // Show a user-friendly validation error message
          if (!suppressErrorToast) {
            toast.error("Please correct the validation errors below")
          }
          return
        }

        // For non-validation errors, show the general error message
        if (!suppressErrorToast) {
          toast.error(error.message || "An error occurred")
        }
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        // Check if the mutation has suppressSuccessToast in meta
        if (mutation.options.meta?.suppressSuccessToast) {
          return
        }

        // Check if the mutation data has a message property
        const data = mutation.state.data as any
        if (data?.message && typeof data.message === "string") {
          toast.success(data.message)
        } else {
          toast.success("Success")
        }
      },
    }),
    defaultOptions: {
      queries: {
        queryFn: async ({ queryKey }) => {
          const baseUrl = "/api/v1/"

          // Check if the last element is a params object (for query parameters)
          const lastElement = queryKey[queryKey.length - 1]
          const hasParams =
            typeof lastElement === "object" &&
            lastElement !== null &&
            !Array.isArray(lastElement)

          // Build the path from queryKey (excluding params object if present)
          const pathElements = hasParams ? queryKey.slice(0, -1) : queryKey
          const slug = pathElements.join("/")

          // Build query string if params exist
          let url = baseUrl + slug
          if (hasParams) {
            const params = lastElement as Record<string, any>
            const searchParams = new URLSearchParams()
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                searchParams.append(key, String(value))
              }
            })
            const queryString = searchParams.toString()
            if (queryString) {
              url += `?${queryString}`
            }
          }

          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
            },
            credentials: "include",
          })

          if (!response.ok) {
            // Get the error message from the response
            let errorMessage = `HTTP ${response.status}: ${response.statusText} returned from ${url}`

            try {
              const errorData = await response.json()
              // Check for detail field first (Huma error format), then message, then error
              if (errorData.detail) {
                errorMessage = errorData.detail
              } else if (errorData.message) {
                errorMessage = errorData.message
              } else if (errorData.error) {
                errorMessage = errorData.error
              } else if (typeof errorData === "string") {
                errorMessage = errorData
              }
            } catch {
              // If response body isn't valid JSON, fall back to status text
            }

            const error = new Error(errorMessage) as any
            error.status = response.status
            error.statusText = response.statusText
            throw error
          }

          return response.json()
        },
        // Stale time of 5 minutes
        staleTime: 5 * 60 * 1000,
        // Cache time of 10 minutes
        gcTime: 10 * 60 * 1000,
        // Retry failed requests with exponential backoff
        // Don't retry 4xx/5xx errors or the /me endpoint
        retry: (failureCount, error) => {
          if (failureCount >= 3) return false
          // Don't retry on 4xx and 5xx errors
          if (error instanceof Error && "status" in error) {
            const status = (error as any).status
            if (status >= 400) return false
          }
          return true
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      mutations: {
        // Don't retry mutations on 4xx/5xx errors
        retry: (failureCount, error) => {
          if (failureCount >= 1) return false
          // Don't retry on 4xx and 5xx errors
          if (error instanceof Error && "status" in error) {
            const status = (error as any).status
            if (status >= 400) return false
          }
          return true
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
    },
  })

interface QueryProviderProps {
  children: ReactNode
}

export const QueryProvider: FC<QueryProviderProps> = ({ children }) => {
  const { addValidationErrors } = useGlobalValidation()

  // Create query client with access to validation context
  const queryClient = useMemo(
    () => createQueryClient(addValidationErrors),
    [addValidationErrors],
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === "development" && (
        <div className="hidden md:block">
          <ReactQueryDevtools initialIsOpen={false} />
        </div>
      )}
    </QueryClientProvider>
  )
}
